import './assets/main.css'

import * as Sentry from '@sentry/vue'
import { createApp } from 'vue'
import App from './App.vue'
import i18n from './utils/i18n.js'
import * as amplitude from '@amplitude/analytics-browser'
import { amplitudeCommonProperties } from './utils/amplitudeCommonProperties.js'
import { getAppName } from './utils/getAppName.js'

const app = createApp(App)
const mode = import.meta.env.MODE

if (!!import.meta.env.VITE_SENTRY_DSN) {
  Sentry.init({
    app,
    dsn: import.meta.env.VITE_SENTRY_DSN,
    environment: import.meta.env.MODE,
    release: __APP_VERSION__,
    integrations: [
      Sentry.browserTracingIntegration(),
      Sentry.httpClientIntegration()
    ],
    sendDefaultPii: true // required for httpClientIntegration
  })
}

if (mode === 'staging' || mode === 'staging-china') {
  import('eruda').then((eruda) => eruda.default.init())
}

if (!!import.meta.env.VITE_AMPLITUDE_API_KEY) {
  const currentHost = window.location.hostname
  const reducedSampleRateHosts =
    import.meta.env.VITE_AMPLITUDE_REDUCED_SAMPLE_RATE_HOSTS?.split(',') || []
  // The env files hold the only copy of this rate: 0 is honored, and anything unusable means no reduction.
  const reducedSampleRate = parseFloat(
    import.meta.env.VITE_AMPLITUDE_REDUCED_SAMPLE_RATE
  )
  const sampleRate =
    reducedSampleRateHosts.includes(currentHost) &&
    reducedSampleRate >= 0 &&
    reducedSampleRate <= 1
      ? reducedSampleRate
      : 1

  // The SDK has no event sampling, so opt the whole page load out instead. Per load, not per user.
  const inSample = Math.random() < sampleRate

  amplitude.init(import.meta.env.VITE_AMPLITUDE_API_KEY, {
    // Every `false` is required: on an object the SDK treats a missing key as enabled.
    autocapture: {
      webVitals: true,
      attribution: false,
      fileDownloads: false,
      formInteractions: false,
      pageUrlEnrichment: false,
      pageViews: false,
      sessions: false
    },
    optOut: !inSample
  })

  getAppName().then((scanApp) => {
    amplitudeCommonProperties.scanApp = scanApp
  })

  amplitude.add({
    name: 'common-properties',
    execute: (event) => {
      // The `$` events carry user or group properties, not event properties, so leave them alone.
      if (event.event_type?.startsWith('$')) {
        return event
      }
      // Spread first so a caller's own value wins.
      event.event_properties = {
        ...amplitudeCommonProperties,
        ...event.event_properties
      }
      return event
    }
  })
}

app.use(i18n)
app.mount('#app')
